import { css } from '@emotion/react'
import Link from 'next/link'
import { colors, font, mq } from 'plume-ui'
import React from 'react'
import { addNewsPicksTrackingParam, resizeBannerImageUrl } from '@/lib/url'
import { NPFeedSearchResult } from '../../../types'
import { LineClampText } from '../../atoms/LineClampText'
import { NPFeedPublisherAndPublishedDate } from './NPFeedPublisherAndPublishedDate'
import { PickersIcons } from './PickersIcons'

type NewsPicksFeedSearchResultItemProps = {
  index?: number
  classPrefix: string
} & NPFeedSearchResult

export const NewsPicksFeedSearchResultItem: React.VFC<
  NewsPicksFeedSearchResultItemProps
> = (props) => {
  const articleUrl = addNewsPicksTrackingParam(props.articleUrl, props.newsId)
  const pickersForDisplay = props.pickers.slice(0, 5)
  const image = props.image || '/static/images/top/np-feed-blank-banner.jpg'
  const bannerLinkId = props.index
    ? `${props.classPrefix}-news-banner-${props.index}`
    : ''
  const titleLinkId = props.index
    ? `${props.classPrefix}-news-title-${props.index}`
    : ''
  return (
    <>
      <div
        css={css`
          ${mq.untilMedium} {
            display: none;
          }
        `}
      >
        <DesktopNewsPicksFeedSearchResultItem
          {...props}
          articleUrl={articleUrl}
          pickers={pickersForDisplay}
          image={image}
          bannerLinkId={bannerLinkId}
          titleLinkId={titleLinkId}
        />
      </div>
      <div
        css={css`
          display: none;
          ${mq.untilMedium} {
            display: block;
          }
        `}
      >
        <MobileNewsPicksFeedSearchResultItem
          {...props}
          articleUrl={articleUrl}
          pickers={pickersForDisplay}
          image={image}
          bannerLinkId={bannerLinkId}
          titleLinkId={titleLinkId}
        />
      </div>
    </>
  )
}

type AnchorPropsForGA = {
  bannerLinkId: string
  titleLinkId: string
}

type NewsPicksFeedSearchResultItemChildProps =
  NewsPicksFeedSearchResultItemProps & AnchorPropsForGA & { image: string }

export const DesktopNewsPicksFeedSearchResultItem: React.VFC<
  NewsPicksFeedSearchResultItemChildProps
> = (props) => {
  return (
    <div
      className="news-item"
      css={css`
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 108px;
        padding: 16px 24px 16px 10px;
      `}
    >
      <div
        css={css`
          display: flex;
          margin: 0 16px 0 0;
        `}
      >
        <div
          className="banner"
          css={css`
            width: 144px;
            margin-right: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            border-radius: 2px;
          `}
        >
          <Link href={props.articleUrl} passHref>
            <a
              id={props.bannerLinkId}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div
                css={css`
                  position: relative;
                  border-radius: 2px;
                  &:after {
                    position: absolute;
                    display: block;
                    content: '';
                    top: 0;
                    left: 0;
                    height: 100%;
                    width: 100%;
                    background-color: transparent;
                    opacity: 0.08;
                    border-radius: 2px;
                    transition: background-color 0.2s ease;
                  }
                  &:hover:after {
                    background-color: #000000;
                    transition: background-color 0.2s ease;
                  }
                `}
              >
                <img
                  src={resizeBannerImageUrl(props.image, 600, 315)}
                  css={css`
                    display: block;
<<<<<<< HEAD
=======
                    width: 100%;
>>>>>>> 3a5d45a37c (企業ページの角丸とか)
                    object-fit: contain;
                    border-radius: 2px;
                    aspect-ratio: 1.91 / 1;
                  `}
                />
              </div>
            </a>
          </Link>
        </div>
        <div>
          <Link href={props.articleUrl} passHref>
            <a
              id={props.titleLinkId}
              className="title"
              target="_blank"
              rel="noopener noreferrer"
              css={css`
                & > span {
                  margin-bottom: 4px;
                  font-weight: ${font.weight.bold};
                  color: ${colors.black10};
                  text-decoration: underline solid transparent;
                  transition: text-decoration 0.2s ease 0s;
                  &:hover {
                    text-decoration: underline solid Currentcolor;
                    transition: text-decoration 0.2s ease 0s;
                  }
                }
              `}
            >
              <LineClampText lineNumber={1} size={'14px'} height={1.5}>
                {props.title}
              </LineClampText>
            </a>
          </Link>
          <NPFeedPublisherAndPublishedDate
            css={css`
              margin-bottom: 4px;
            `}
            publisher={props.publisher}
            publishedDate={props.publishedDate}
            fontSize={'12px'}
            lineHeight={1.25}
          />
          {props.description && (
            <LineClampText
              className="description"
              css={css`
                color: ${colors.black30};
              `}
              lineNumber={2}
              size={'12px'}
              height={1.5}
            >
              {props.description}
            </LineClampText>
          )}
        </div>
      </div>
      <div
        css={css`
          height: 100%;
          width: 214px;
          display: flex;
          align-items: center;
          flex-shrink: 0;
        `}
      >
        <div
          css={css`
            width: 116px;
            margin: 0 16px 0 4px;
            flex-shrink: 0;
          `}
        >
          <PickersIcons
            className="np-pickers"
            pickers={props.pickers}
          ></PickersIcons>
        </div>
        <div
          css={css`
            color: ${colors.black30};
            text-align: left;
          `}
        >
          <span
            className="pick-count"
            css={css`
              font-weight: ${font.weight.bold};
              margin-right: 4px;
            `}
          >
            {props.pickCount}
          </span>
          <span
            css={css`
              font-size: 12px;
            `}
          >
            Picks
          </span>
        </div>
      </div>
    </div>
  )
}

export const MobileNewsPicksFeedSearchResultItem: React.VFC<
  NewsPicksFeedSearchResultItemChildProps
> = (props) => {
  return (
    <div
      className="news-item"
      css={css`
        padding: 16px 24px;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        `}
      >
        <div
          css={css`
            margin-right: 8px;
          `}
        >
          <Link href={props.articleUrl} passHref>
            <a
              id={props.titleLinkId}
              className="title"
              target="_blank"
              rel="noopener noreferrer"
              css={css`
                & > span {
                  font-weight: ${font.weight.bold};
                  color: ${colors.black10};
                  text-decoration: underline solid transparent;
                  transition: text-decoration 0.2s ease 0s;
                  &:hover {
                    text-decoration: underline solid Currentcolor;
                    transition: text-decoration 0.2s ease 0s;
                  }
                }
              `}
            >
              <LineClampText lineNumber={2} size={'15px'} height={1.5}>
                {props.title}
              </LineClampText>
            </a>
          </Link>
        </div>
        <div
          className="banner"
          css={css`
            height: 59px;
            width: 112px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            overflow: hidden;
          `}
        >
          <Link href={props.articleUrl} passHref>
            <a
              id={props.bannerLinkId}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div
                css={css`
                  position: relative;
                  border-radius: 2px;
                  &:after {
                    position: absolute;
                    display: block;
                    content: '';
                    top: 0;
                    left: 0;
                    height: 100%;
                    width: 100%;
                    background-color: transparent;
                    border-radius: 2px;
                    opacity: 0.08;
                    transition: background-color 0.2s ease;
                  }
                  &:hover:after {
                    background-color: #000000;
                    transition: background-color 0.2s ease;
                  }
                `}
              >
                <img
                  src={resizeBannerImageUrl(props.image, 600, 315)}
                  css={css`
                    display: block;
                    width: 100%;
                    object-fit: contain;
                    aspect-ratio: 1.91 / 1;
                  `}
                />
              </div>
            </a>
          </Link>
        </div>
      </div>
      <NPFeedPublisherAndPublishedDate
        css={css`
          margin-bottom: 10px;
        `}
        publisher={props.publisher}
        publishedDate={props.publishedDate}
        fontSize={'12px'}
        lineHeight={1.25}
      />
      {props.description && (
        <LineClampText
          className="description"
          css={css`
            color: ${colors.black30};
            margin-bottom: 16px;
          `}
          lineNumber={4}
          size={'13px'}
          height={1.5}
        >
          {props.description}
        </LineClampText>
      )}
      <div
        css={css`
          display: flex;
          align-items: flex-end;
        `}
      >
        <PickersIcons
          className="np-pickers"
          pickers={props.pickers}
        ></PickersIcons>
        <div
          css={css`
            color: ${colors.black30};
            text-align: left;
            padding-bottom: 4px;
            margin-left: 8px;
          `}
        >
          <span
            className="pick-count"
            css={css`
              font-weight: ${font.weight.bold};
              font-size: 16px;
              line-height: 100%;
              margin-right: 2px;
            `}
          >
            {props.pickCount}
          </span>
          <span
            css={css`
              font-size: 14px;
              line-height: 100%;
            `}
          >
            Picks
          </span>
        </div>
      </div>
    </div>
  )
}
